import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import type { ReactNode } from "react";

const HEX_BAR_URL = "https://cdn.queen.one/connect/ui/hex-bar.png";

function ArrowIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ verticalAlign: "middle" }}
    >
      <path
        d="M6.89941 12L11.1992 6.70743L6.89941 1.41485"
        stroke="currentColor"
        strokeWidth="1.53883"
      />
      <path
        d="M11.0001 6.70898L0.983398 6.70898"
        stroke="currentColor"
        strokeWidth="1.53883"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15 5L5 15M5 5l10 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const TitleBar = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  paddingBottom: 0,
});

const StyledIconButton = styled(IconButton)({
  marginTop: -20,
  marginRight: -25,
});

const StepLabel = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  display: "block",
  color: theme.palette.text.secondary,
  fontWeight: "bold",
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#F5F0F9",
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme.palette.grey[600]}`,
    },
  },
}));

const CardStack = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  flexWrap: "wrap",
  marginTop: theme.spacing(5),
  gap: theme.spacing(6),
  padding: theme.spacing(2),
}));

const ChoiceCard = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "isDisabled",
})<{ selected?: boolean; isDisabled?: boolean }>(
  ({ theme, selected, isDisabled }) => ({
    flex: "1 1 280px",
    minWidth: 280,
    maxWidth: 400,
    alignItems: "center",
    justifyContent: "start",
    gap: theme.spacing(5),
    padding: theme.spacing(5, 7),
    borderRadius: "8px",
    border: selected
      ? `2px solid ${theme.palette.common.black}`
      : `1px solid ${theme.palette.grey[300]}`,
    background: theme.palette.background["paper-elevation-1"],
    cursor: isDisabled ? "not-allowed" : "pointer",
    transition: "border-color 0.2s ease",
    opacity: isDisabled ? 0.5 : 1,
    pointerEvents: isDisabled ? "none" : "auto",
    "&:hover, &:focus-visible": {
      borderColor: isDisabled ? undefined : theme.palette.common.black,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  }),
);

const CardTitle = styled(Typography)({});

const CardDescription = styled(Typography)({
  textAlign: "center",
});

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
  marginTop: 0,
});

export interface OrionChoiceDialogueChoice<T extends string = string> {
  id: T;
  title: string;
  description: string;
  icon: ReactNode;
  disabled?: boolean;
}

export interface OrionChoiceDialogueProps<T extends string = string> {
  open?: boolean;
  onClose: () => void;
  asDialog?: boolean;

  title: string;
  subtitle?: string;
  stepLabel?: string;

  inputLabel: string;
  inputPlaceholder?: string;
  inputHelperText?: ReactNode;
  inputValue: string;
  onInputChange: (value: string) => void;
  inputError?: string;
  inputDisabled?: boolean;
  inputId?: string;

  choices: OrionChoiceDialogueChoice<T>[];
  onSelect: (choiceId: T) => void;

  cardMinHeight?: number;
}

export function OrionChoiceDialogue<T extends string = string>({
  open = true,
  onClose,
  asDialog = true,
  title,
  subtitle,
  stepLabel,
  inputLabel,
  inputPlaceholder,
  inputHelperText,
  inputValue,
  onInputChange,
  inputError,
  inputDisabled,
  inputId,
  choices,
  onSelect,
  cardMinHeight,
}: OrionChoiceDialogueProps<T>) {
  const theme = useTheme();

  const handleSelectChoice = (choiceId: T) => {
    onSelect(choiceId);
  };

  const handleKeyDown =
    (choiceId: T, disabled?: boolean) =>
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleSelectChoice(choiceId);
      }
    };

  const content = (
    <>
      <TitleBar>
        <Typography variant="h4" component="h4">
          {title}
        </Typography>
        <StyledIconButton
          onClick={onClose}
          size="large"
          aria-label={`Close ${title.toLowerCase()}`}
        >
          <CloseIcon />
        </StyledIconButton>
      </TitleBar>

      <StyledDialogContent>
        {stepLabel && <StepLabel variant="overline">{stepLabel}</StepLabel>}
        {subtitle && <Subtitle variant="h6">{subtitle}</Subtitle>}

        <StyledInputLabel htmlFor={inputId}>{inputLabel}</StyledInputLabel>
        <StyledTextField
          id={inputId}
          autoFocus
          size="small"
          fullWidth
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          disabled={inputDisabled}
          error={!!inputError}
          helperText={inputError || inputHelperText}
          placeholder={inputPlaceholder}
          onKeyDown={(e) => {
            if (e.key === "Enter" && choices.length > 0) {
              e.preventDefault();
              const firstEnabledChoice = choices.find((c) => !c.disabled);
              if (firstEnabledChoice) {
                handleSelectChoice(firstEnabledChoice.id);
              }
            }
          }}
        />

        <CardStack
          sx={
            cardMinHeight
              ? { "& > *": { minHeight: cardMinHeight } }
              : undefined
          }
        >
          {choices.map((choice) => (
            <ChoiceCard
              key={choice.id}
              role="button"
              tabIndex={choice.disabled ? -1 : 0}
              aria-label={choice.title}
              aria-disabled={choice.disabled}
              isDisabled={choice.disabled}
              onClick={() => {
                if (!choice.disabled) handleSelectChoice(choice.id);
              }}
              onKeyDown={handleKeyDown(choice.id, choice.disabled)}
            >
              <CardTitle variant="h5">
                {choice.title} <ArrowIcon />
              </CardTitle>
              {choice.icon}
              <CardDescription variant="body1" color="text.secondary">
                {choice.description}
              </CardDescription>
            </ChoiceCard>
          ))}
        </CardStack>
      </StyledDialogContent>
    </>
  );

  if (!asDialog) {
    return content;
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            display: "flex",
            flexDirection: "column",
            width: "884px",
            maxWidth: "calc(100vw - 48px)",
            padding: 0,
            boxSizing: "border-box",
            gap: 0,
            borderRadius: "16px",
            background: theme.palette.common.white,
            overflow: "hidden",
          },
        },
      }}
    >
      <img
        src={HEX_BAR_URL}
        alt=""
        style={{ width: "100%", height: "8px", objectFit: "cover" }}
      />
      <Stack sx={{ flex: 1, p: 8 }}>{content}</Stack>
    </Dialog>
  );
}
